import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import useLabels from 'infrastructure/js/hooks/useLabels';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import ScanInputStandalone from '../../Mobile/Common/ScanInput/scanInputStandalone';

import './assignRemoveAssetsTagsDialog.scss';

// statuses: 'empty' | 'loading' | 'assetEmpty' | 'tagEmpty' | 'complete' | 'error'


const extractTagKey = (value) => {
  const slashIndex = value.lastIndexOf('/');
  return slashIndex !== -1 ? value.slice(slashIndex + 1) : value;
};

export function AssignRemoveAssetsTagsDialog({ actions, sData }) {
  const labels = useLabels('mat.locationPage.view.assignRemoveAssetsTags.dialog.');
  const dialogLabels = useLabels('mat.dialog.');
  const dialogMode = sData?.get('dialogData')?.mode || 'ASSIGN';
  const isRemoveMode = dialogMode === 'REMOVE';

  const rowIdRef = useRef(0);
  const listRef = useRef(null);
  const createRow = () => ({ id: rowIdRef.current++, assetId: '', tag: '', status: 'empty', assetError: '', tagError: '' });

  const [rows, setRows] = useState(() => [createRow()]);
  const [focusRowId, setFocusRowId] = useState(0);
  const [focusField, setFocusField] = useState(isRemoveMode ? 'tag' : 'asset');
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  const isDuplicateAssetId = (assetId, currentRowId) =>
    rows.some(r => r.id !== currentRowId && r.assetId === assetId && r.assetId !== '');

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [rows.length]);

  const updateRow = (rowId, changes) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, ...changes } : r));
  };

  const appendRow = () => {
    const newRow = createRow();
    setRows(prev => [...prev, newRow]);
    setFocusRowId(newRow.id);
    setFocusField(isRemoveMode ? 'tag' : 'asset');
  };

  const completeRow = (rowId) => {
    updateRow(rowId, { status: 'complete' });
    appendRow();
  };

  // ── ASSIGN mode ──────────────────────────────────────────────────────────

  const handleAssetSubmit = (rowId, assetId) => {
    setDuplicateWarning(false);

    if (isDuplicateAssetId(assetId, rowId)) {
      setDuplicateWarning(true);
      updateRow(rowId, { assetId: '' });
      return;
    }

    updateRow(rowId, { assetId, status: 'loading', tag: '', assetError: '', tagError: '' });

    actions.fetchAsset({ assetBusinessId: assetId }, {
      onSuccess: (assetData) => {
        const existingTag = assetData?.tagKey;
        if (existingTag) {
          updateRow(rowId, { tag: existingTag, assetError: '' });
          completeRow(rowId);
        } else {
          updateRow(rowId, { status: 'tagEmpty', assetError: '' });
          setFocusRowId(rowId);
          setFocusField('tag');
        }
      },
      onError: (err) => updateRow(rowId, { status: 'error', assetError: err }),
    });
  };

  const handleAssetChange = (rowId, val) => {
    updateRow(rowId, { assetId: val, ...(!val && { tag: '', status: 'empty', assetError: '', tagError: '' }) });
    setFocusRowId(rowId);
    setFocusField('asset');
  };

  const handleTagSubmit = (rowId, tag) => {
    const row = rows.find(r => r.id === rowId);
    if (!row?.assetId) {
      return;
    }
    const tagKey = extractTagKey(tag);
    updateRow(rowId, { tag: tagKey, status: 'loading', tagError: '' });
    actions.assignTag(row.assetId, tagKey, {
      onSuccess: () => completeRow(rowId),
      onError: (err) => updateRow(rowId, { status: 'error', tagError: err }),
    });
  };

  // ── REMOVE mode ───────────────────────────────────────────────────────────

  const handleTagRemove = (rowId, tag) => {
    const tagKey = extractTagKey(tag);
    updateRow(rowId, { tag: tagKey, status: 'loading', assetId: '', tagError: '' });

    actions.removeTag(tagKey, {
      onSuccess: (tagData) => {
        const assetBusinessId = tagData?.assetBusinessId;
        if (assetBusinessId) {
          updateRow(rowId, { assetId: assetBusinessId, tagError: ''});
          completeRow(rowId);
          return;
        }
        // updateRow(rowId, { assetId: labels.get('result.noRelevantAssetFound'), status: 'noAsset', tagError: '' });
        updateRow(rowId, { assetId: '', status: 'assetEmpty', tagError: '' });
      },
      onError: (err) => updateRow(rowId, { status: 'error', tagError: err }),
    });
  };


  const onHide = () => {
    actions.hide();
  };

  const getDialogButtons = () => {
    return {
      left: [],
      right: [
        {
          id: 'close',
          text: dialogLabels.get('close'),
          bsStyle: 'primary',
          action: onHide,
        },
      ],
    };
  };

  const renderAssignRow = (row) => {
    const isComplete = row.status === 'complete';
    const isLoading = row.status === 'loading';
    const isAssetDisabled = isComplete || isLoading;
    const isTagDisabled = row.status === 'empty' || isComplete || isLoading || !!row.assetError;

    return (
      <>
        <ScanInputStandalone
          id={`assetIdInput-${row.id}`}
          placeholder={labels.get('placeholder.assetId')}
          value={row.assetId}
          onChange={(val) => handleAssetChange(row.id, val)}
          onSubmit={(val) => handleAssetSubmit(row.id, val)}
          disabled={isAssetDisabled}
          error={row.assetError}
          autoFocus={focusRowId === row.id && focusField === 'asset'}
        />
        <ScanInputStandalone
          id={`tagInput-${row.id}`}
          placeholder={labels.get('placeholder.tag')}
          value={row.tag}
          onChange={(val) => updateRow(row.id, { tag: val, ...(!val && { tagError: ''}) })}
          onSubmit={(val) => handleTagSubmit(row.id, val)}
          disabled={isTagDisabled}
          error={row.tagError}
          autoFocus={focusRowId === row.id && focusField === 'tag'}
        />
        <i className={cn('fa fa-check complete-icon', {'complete': isComplete})} />
      </>
    );
  };

  const renderRemoveRow = (row) => {
    const isComplete = row.status === 'complete';
    const isLoading = row.status === 'loading';
    const isTagActive = row.status === 'empty' || row.status === 'assetEmpty' || row.status === 'error';

    return (
      <>
        <ScanInputStandalone
          id={`tagInput-${row.id}`}
          placeholder={labels.get('placeholder.tag')}
          value={row.tag}
          onChange={(val) => updateRow(row.id, { tag: val })}
          onSubmit={(val) => handleTagRemove(row.id, val)}
          disabled={!isTagActive || isComplete || isLoading}
          error={row.tagError}
          autoFocus={focusRowId === row.id && focusField === 'tag'}
          selectAll={row.status === 'assetEmpty'}
        />
        <ScanInputStandalone
          id={`assetIdInput-${row.id}`}
          placeholder=""
          value={row.assetId}
          disabled={true}
          error={row.assetError}
          autoFocus={false}
        />
        <span className={cn('removed-label', {'complete': isComplete})}>{labels.get('removed')}</span>
      </>
    );
  };

  const titleText = isRemoveMode ? labels.get('header.title.removeTag') : labels.get('header.title.assignTag');
  const titleComponent = (
    <div className="dialog-title-with-warning">
      <label>{titleText}</label>
      {duplicateWarning && (
        <span className="duplicate-warning-message">{labels.get('warning.duplicateAsset')}</span>
      )}
    </div>
  );

  return (
    <Dialog
      id="assign-remove-assets-tags-dialog"
      className="assign-remove-assets-tags-dialog"
      titleComponent={titleComponent}
      show={true}
      onHide={onHide}
      sData={sData}
      footerButtons={getDialogButtons()}
    >
      {isRemoveMode && <div className='top-title title-label'>{labels.get('top.title')}</div>}

      <div className={cn('list-title', {'remove-mode': isRemoveMode})}>
        <div className='title-label'>{labels.get('assetId')}</div>
        <div className='title-label'>{labels.get('tag')}</div>
      </div>

      <div className='asset-tag-list' ref={listRef}>
        {rows.map((row) => (
          <div key={row.id} className="asset-tag-item">
            {isRemoveMode ? renderRemoveRow(row) : renderAssignRow(row)}
          </div>
        ))}
      </div>
    </Dialog>
  );
}

AssignRemoveAssetsTagsDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
};

export default AssignRemoveAssetsTagsDialog;
